import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Paging,
  PopConfirm,
  Select,
  Table,
  Tag,
  Wrapper,
  useBreadcrumbRoutes,
  usePaging,
} from '@maxtropy/components';
import { Space } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ALARM_LEVEL_CONFIG,
  MONITOR_OBJECT_TYPE_CONFIG,
  RULE_STATUS_CONFIG,
  alarmLevelOptions,
  monitorObjectTypeOptions,
  ruleStatusOptions,
} from './constants';
import { getMockRules } from './mockData';
import styles from './index.module.scss';
import { DispatchDeviationAlarmRule, RuleFilters, RuleStatus } from './types';

const DispatchDeviationRule = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [form] = Form.useForm<RuleFilters>();
  const [rules, setRules] = useState(getMockRules);
  const [filters, setFilters] = useState<RuleFilters>({});
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;

  const monitorObjectOptions = useMemo(
    () =>
      Array.from(new Set(rules.map(item => item.monitorObject))).map(value => ({
        label: value,
        value,
      })),
    [rules]
  );

  const filteredRules = useMemo(() => {
    const keyword = filters.keyword?.trim().toLowerCase();

    return rules.filter(item => {
      const matchesKeyword =
        !keyword || item.code.toLowerCase().includes(keyword) || item.name.toLowerCase().includes(keyword);
      const matchesLevel = !filters.alarmLevel || item.alarmLevel === filters.alarmLevel;
      const matchesType = !filters.monitorObjectType || item.monitorObjectType === filters.monitorObjectType;
      const matchesObject = !filters.monitorObject || item.monitorObject === filters.monitorObject;
      const matchesStatus = !filters.status || item.status === filters.status;

      return matchesKeyword && matchesLevel && matchesType && matchesObject && matchesStatus;
    });
  }, [filters, rules]);

  useEffect(() => {
    setTotalCount(filteredRules.length);
    const lastPage = Math.max(1, Math.ceil(filteredRules.length / pageSize));
    if (pageOffset > lastPage) {
      setPageOffset(lastPage);
    }
  }, [filteredRules.length, pageOffset, pageSize, setPageOffset, setTotalCount]);

  const pageData = useMemo(() => {
    const start = (pageOffset - 1) * pageSize;
    return filteredRules.slice(start, start + pageSize);
  }, [filteredRules, pageOffset, pageSize]);

  const toggleStatus = (record: DispatchDeviationAlarmRule) => {
    setRules(current =>
      current.map(item =>
        item.id === record.id
          ? {
              ...item,
              status: item.status === RuleStatus.ENABLED ? RuleStatus.DISABLED : RuleStatus.ENABLED,
              lastOperationTime: '2025-09-12 16:58:45',
            }
          : item
      )
    );
  };

  const deleteRule = (id: number) => {
    setRules(current => current.filter(item => item.id !== id));
  };

  const columns = useMemo<ColumnType<DispatchDeviationAlarmRule>[]>(
    () => [
      {
        title: '规则编号',
        dataIndex: 'code',
        width: 130,
        ellipsis: true,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '规则名称',
        dataIndex: 'name',
        width: 150,
        ellipsis: true,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '监听对象类型',
        dataIndex: 'monitorObjectType',
        width: 130,
        render: (value: DispatchDeviationAlarmRule['monitorObjectType']) => (
          <EllipsisSpan value={MONITOR_OBJECT_TYPE_CONFIG[value]} />
        ),
      },
      {
        title: '监听对象',
        dataIndex: 'monitorObject',
        width: 150,
        ellipsis: true,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '报警等级',
        dataIndex: 'alarmLevel',
        width: 100,
        render: (value: DispatchDeviationAlarmRule['alarmLevel']) => {
          const config = ALARM_LEVEL_CONFIG[value];
          return (
            <Tag border="solid" color={config.color} className={styles.tag}>
              {config.label}
            </Tag>
          );
        },
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 90,
        render: (value: RuleStatus) => {
          const config = RULE_STATUS_CONFIG[value];
          return (
            <Tag border="solid" color={config.color} className={styles.tag}>
              {config.label}
            </Tag>
          );
        },
      },
      {
        title: '最后操作人',
        dataIndex: 'lastOperator',
        width: 130,
        ellipsis: true,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '最后操作时间',
        dataIndex: 'lastOperationTime',
        width: 180,
        ellipsis: true,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 220,
        render: (_, record) => (
          <Space size={16}>
            <Button
              type="link"
              className={styles.actionButton}
              onClick={() => navigate(`/dispatch/control/deviation-rule/${record.id}/edit`)}
            >
              编辑
            </Button>
            <Button
              type="link"
              className={styles.actionButton}
              onClick={() => navigate(`/dispatch/control/deviation-rule/${record.id}/view`)}
            >
              查看
            </Button>
            <PopConfirm
              title={`确认${record.status === RuleStatus.ENABLED ? '禁用' : '启用'}该规则？`}
              okText="确定"
              cancelText="取消"
              onConfirm={() => toggleStatus(record)}
            >
              <Button type="link" className={styles.actionButton}>
                {record.status === RuleStatus.ENABLED ? '禁用' : '启用'}
              </Button>
            </PopConfirm>
            <PopConfirm
              title="确认删除该规则？删除后不可恢复。"
              okText="确定"
              cancelText="取消"
              onConfirm={() => deleteRule(record.id)}
            >
              <Button type="link" className={styles.deleteButton}>
                删除
              </Button>
            </PopConfirm>
          </Space>
        ),
      },
    ],
    [navigate]
  );

  const filterContent = (
    <CustomFilter<RuleFilters>
      form={form}
      onFinish={values => {
        setPageOffset(1);
        setFilters(values);
      }}
      onReset={() => {
        setPageOffset(1);
        setFilters({});
      }}
    >
      <Form.Item name="keyword" label="编号/名称">
        <Input allowClear placeholder="请输入名称" />
      </Form.Item>
      <Form.Item name="alarmLevel" label="报警等级">
        <Select allowClear placeholder="请选择" options={alarmLevelOptions} />
      </Form.Item>
      <Form.Item name="monitorObjectType" label="监听对象类型">
        <Select allowClear placeholder="请选择" options={monitorObjectTypeOptions} />
      </Form.Item>
      <Form.Item name="monitorObject" label="监听对象">
        <Select allowClear placeholder="请选择" options={monitorObjectOptions} showSearch optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="status" label="状态">
        <Select allowClear placeholder="请选择" options={ruleStatusOptions} />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes ?? []} filters={filterContent} className={styles.wrapper}>
      <div className={styles.toolbar}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/dispatch/control/deviation-rule/create')}
        >
          新建规则
        </Button>
      </div>
      <Table sticky rowKey="id" scroll={{ x: 1280 }} columns={columns} dataSource={pageData} />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default DispatchDeviationRule;
